import { useCallback, useEffect, useRef, useState } from 'react';
import { baseUrl } from './useFetch';

interface TaskNotification {
    taskId: number;
    projectId: number;
    projectName: string;
    taskName: string;

    serviceId: number;
    serviceName: string;

    milestoneId: number;
    milestoneName: string;

    priorityId: number;
    priorityName: string;

    assignedToId: number;
    assignedToName: string;

    reviewerId: number;
    reviewerName: string;

    statusId: number;
    statusName: string;

    date: string;
    startTime: string;
    endTime: string;

    status: 'Upcoming' | 'Running' | 'Overdue';
    message: string;
}

interface TaskNotificationResponse {
    success: boolean;
    message: string;
    notifications: TaskNotification[];
}

export const useTaskReminder = (token: string | undefined) => {
    const [reminderTask, setReminderTask] =
        useState<TaskNotification | null>(null);

    /*
     * Store dismissed task IDs.
     *
     * Example:
     * Task 20 is dismissed -> 20 is added here.
     */
    const dismissedRef = useRef<Set<number>>(new Set());

    const checkTaskNotifications = useCallback(async () => {
        if (!token) {
            setReminderTask(null);
            return;
        }

        try {
            /*
             * Get browser's timezone.
             *
             * Example:
             * Asia/Kolkata
             */
            const timeZoneId =
                Intl.DateTimeFormat().resolvedOptions().timeZone;

            const response = await fetch(
                `${baseUrl}ProjectTasks/TaskNotification?timeZoneId=${encodeURIComponent(
                    timeZoneId
                )}`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                console.error(
                    'Task notification API failed:',
                    response.status
                );

                return;
            }

            const data: TaskNotificationResponse =
                await response.json();

            if (
                !data.success ||
                !Array.isArray(data.notifications)
            ) {
                setReminderTask(null);
                return;
            }

            /*
             * Backend already handles:
             *
             * Upcoming:
             * Start time - 15 minutes
             *        to
             * Start time
             *
             * Running:
             * Start time
             *        to
             * End time
             *
             * Overdue:
             * After end time
             *
             * Therefore frontend does NOT calculate
             * the time difference again.
             */

            const availableNotifications =
                data.notifications.filter(
                    (task) =>
                        !dismissedRef.current.has(task.taskId)
                );

            /*
             * If there are no notifications,
             * remove the popup.
             */
            if (availableNotifications.length === 0) {
                setReminderTask(null);
                return;
            }

            /*
             * API is already sorted by date and startTime.
             *
             * Take the first available notification.
             */
            const notification =
                availableNotifications[0];

            setReminderTask(notification);
        } catch (error) {
            console.error(
                'Task reminder check failed:',
                error
            );
        }
    }, [token]);

    useEffect(() => {
        if (!token) {
            setReminderTask(null);
            return;
        }

        /*
         * Check immediately when component loads.
         */
        checkTaskNotifications();

        /*
         * Check every 1 minute.
         *
         * This means:
         *
         * 09:44 -> no notification
         * 09:45 -> notification
         * 09:46 -> notification
         * ...
         * 09:59 -> notification
         * 10:00 -> backend changes it to Running
         */
        const interval = setInterval(() => {
            checkTaskNotifications();
        }, 60 * 1000);

        return () => {
            clearInterval(interval);
        };
    }, [token, checkTaskNotifications]);

    /*
     * Dismiss current notification.
     */
    const dismissReminder = useCallback(() => {
        if (!reminderTask) {
            return;
        }

        dismissedRef.current.add(
            reminderTask.taskId
        );

        setReminderTask(null);
    }, [reminderTask]);

    return {
        reminderTask,
        dismissReminder,
        refreshTaskNotifications: checkTaskNotifications,
    };
};